import * as React from 'react';
import { observer } from 'mobx-react-lite';
import TextareaAutosize from 'react-textarea-autosize';
import { Button, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';
import { SendOutlined } from '@ant-design/icons';
import { ChatbotStore } from '../../stores';
import { ChatbotSettingsValues } from '../../types';
import './ChatbotInput.less';

type Props = {
    store: ChatbotStore;
    settingsForm: FormInstance<ChatbotSettingsValues>;
};

const ChatbotInput: React.FC<Props> = ({ store, settingsForm }) => {
    const [message, setMessage] = React.useState('');
    const [showTooltip, setShowTooltip] = React.useState(false);

    const ref = React.useRef<HTMLTextAreaElement>(null);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const isDisabled = React.useMemo(
        () => !store.smartIndexEnabled || store.isBusy || !message.trim().length,
        [store.isBusy, message, store.smartIndexEnabled]
    );

    React.useEffect(() => {
        if (store.dialogVisible && ref.current) {
            ref.current.focus();
        }
    }, [store.dialogVisible]);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const showTooltipIfNeeded = () => {
        if (store.smartIndexEnabled) {
            return;
        }

        if (showTooltip || timeoutRef.current) {
            return;
        }

        setShowTooltip(true);

        timeoutRef.current = setTimeout(() => {
            setShowTooltip(false);
            timeoutRef.current = null;
        }, 3000);
    };

    const askQuestion = () => {
        if (isDisabled) {
            return showTooltipIfNeeded();
        }

        if (!store.settings.values.instructWorkflowId) {
            store.setSettingsVisible(true);
            setTimeout(() => settingsForm.validateFields(), 300);
            return;
        }

        store.askQuestion(message.trim());
        setMessage('');
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(event.target.value);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const enterPressed = event.key === 'Enter';
        const newLinePressed = event.metaKey || event.shiftKey;

        if (enterPressed && !newLinePressed) {
            event.preventDefault();
            askQuestion();
            return;
        }

        if (event.key === 'ArrowUp' && store.lastSentMessage) {
            setMessage(store.lastSentMessage);
        }
    };

    return (
        <div className="chatbot-input-container">
            <TextareaAutosize
                className="chatbot-input"
                placeholder="Ask a question..."
                ref={ref}
                minRows={1}
                maxRows={6}
                value={message}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
            />

            <Tooltip
                key={showTooltip.toString()}
                title={
                    store.smartIndexEnabled
                        ? 'Send question'
                        : 'Enable Smart index in project settings and reparse document'
                }
                placement={store.smartIndexEnabled ? 'topRight' : 'topLeft'}
                visible={showTooltip ? true : undefined}
            >
                <Button type="primary" disabled={isDisabled} onClick={askQuestion}>
                    <SendOutlined />
                </Button>
            </Tooltip>
        </div>
    );
};

export default observer(ChatbotInput);
